// Временное переопределение стиля слоёв Mapbox на время съёмки кадра при
// экспорте в растр. Аналог exportTitle, но для paint/layout: слой может
// объявить свойство exportStyle, чтобы на печати выглядеть иначе, чем на
// экране (например, убрать штриховки/пунктиры). Строго опционально — слои без
// exportStyle не затрагиваются, поведение экспорта для них не меняется.
//
// Форма exportStyle — карта { [mapboxLayerId]: { paint?, layout? } }, где ключ —
// id слоя Mapbox (сам слой или любой из его additionalLayers), а значение —
// оверрайды paint/layout. Так один exportStyle верхнего слоя может переопределить
// стиль своих под-слоёв. Поддерживается и форма-функция (ctx) => такая карта
// для динамики от контролов (по аналогии с exportTitle).

// Разворачивает exportStyle слоя в карту id → { paint, layout }.
const resolveExportStyle = (exportStyle, ctx) => {
    const resolved = typeof exportStyle === 'function' ? exportStyle(ctx) : exportStyle;
    if (!resolved || typeof resolved !== 'object') {
        return null;
    }

    return resolved;
};

// Применяет exportStyle ко всем видимым слоям, у которых он задан. Возвращает
// список записей для восстановления исходных значений. Пропускает слои без
// exportStyle и id, которых нет на карте.
export const applyExportStyles = (map, displayedLayers, ctx = {}) => {
    const saved = [];
    if (!map) {
        return saved;
    }

    (displayedLayers || []).forEach(layer => {
        if (!layer || !layer.exportStyle) {
            return;
        }
        const styleById = resolveExportStyle(layer.exportStyle, ctx);
        if (!styleById) {
            return;
        }
        Object.entries(styleById).forEach(([ id, override ]) => {
            if (!override || !map.getLayer(id)) {
                return;
            }
            Object.entries(override.paint || {}).forEach(([ key, value ]) => {
                saved.push({ id, kind: 'paint', key, prev: map.getPaintProperty(id, key) });
                map.setPaintProperty(id, key, value);
            });
            Object.entries(override.layout || {}).forEach(([ key, value ]) => {
                saved.push({ id, kind: 'layout', key, prev: map.getLayoutProperty(id, key) });
                map.setLayoutProperty(id, key, value);
            });
        });
    });

    return saved;
};

// Возвращает исходные значения свойств, сохранённые applyExportStyles.
export const restoreExportStyles = (map, saved) => {
    if (!map || !saved) {
        return;
    }

    saved.forEach(({ id, kind, key, prev }) => {
        if (!map.getLayer(id)) {
            return;
        }
        if (kind === 'paint') {
            map.setPaintProperty(id, key, prev);
        } else {
            map.setLayoutProperty(id, key, prev);
        }
    });
};

// Ждёт, пока карта закончит перерисовку после смены стиля, но не дольше timeout
// мс — на случай, если 'idle' не наступит (например, карта уже была idle).
// Полный 'idle' нужен, чтобы применённый paint успел попасть в буфер кадра:
// одного 'render' недостаточно (paint виден не сразу).
const waitIdle = (map, timeout = 1500) => new Promise(resolve => {
    if (!map) {
        resolve();

        return;
    }

    let done = false;
    const finish = () => {
        if (done) {
            return;
        }
        done = true;
        map.off('idle', finish);
        resolve();
    };

    map.once('idle', finish);
    setTimeout(finish, timeout);
});

// Оборачивает съёмку кадра: применяет exportStyle к видимым слоям, ждёт
// перерисовки, выполняет fn (собственно сборку растра), затем ГАРАНТИРОВАННО
// восстанавливает исходный стиль. Если ни у одного слоя нет exportStyle —
// ожидания перерисовки нет, кадр снимается как раньше.
export const withExportStyles = async (map, displayedLayers, ctx, fn) => {
    const saved = applyExportStyles(map, displayedLayers, ctx);
    try {
        if (saved.length) {
            await waitIdle(map);
        }

        return await fn();
    } finally {
        restoreExportStyles(map, saved);
    }
};
